import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Briefcase, Layers, CalendarDays, Wand2, ChevronRight, Sparkles, ShieldCheck, Activity } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';

import BreakManagement from './BreakManagement';
import WorkShiftManagement from './WorkShiftManagement';
import ShiftGroupManagement from './ShiftGroupManagement';
import WorkPeriodManagement from './WorkPeriodManagement';
import ShiftWizardModal from '../../components/common/ShiftWizardModal';

export default function SchedulingDefinitionsPage() {
    const [activeTab, setActiveTab] = useState('breaks');
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [counts, setCounts] = useState({
        breaks: 0,
        shifts: 0,
        groups: 0,
        periods: 0
    });

    const fetchSummaryCounts = useCallback(async () => {
        try {
            const [breaksRes, shiftsRes, groupsRes, periodsRes] = await Promise.allSettled([
                apiClient('scheduling/breaks', { method: 'GET' }),
                apiClient('scheduling/shifts', { method: 'GET' }),
                apiClient('scheduling/groups', { method: 'GET' }),
                apiClient('scheduling/periods', { method: 'GET' })
            ]);
            setCounts({
                breaks: breaksRes.status === 'fulfilled' && Array.isArray(breaksRes.value) ? breaksRes.value.length : 0,
                shifts: shiftsRes.status === 'fulfilled' && Array.isArray(shiftsRes.value) ? shiftsRes.value.length : 0,
                groups: groupsRes.status === 'fulfilled' && Array.isArray(groupsRes.value) ? groupsRes.value.length : 0,
                periods: periodsRes.status === 'fulfilled' && Array.isArray(periodsRes.value) ? periodsRes.value.length : 0,
            });
        } catch {
            // Yetki kısıtı varsa sessizce geç
        }
    }, []);

    useEffect(() => {
        fetchSummaryCounts();
    }, [fetchSummaryCounts, refreshKey]);

    const renderActiveTabContent = () => {
        switch (activeTab) {
            case 'breaks':
                return <BreakManagement key={`breaks-${refreshKey}`} onDataChange={fetchSummaryCounts} />;
            case 'shifts':
                return <WorkShiftManagement key={`shifts-${refreshKey}`} onDataChange={fetchSummaryCounts} />;
            case 'groups':
                return <ShiftGroupManagement key={`groups-${refreshKey}`} onDataChange={fetchSummaryCounts} />;
            case 'periods':
                return <WorkPeriodManagement key={`periods-${refreshKey}`} onDataChange={fetchSummaryCounts} />;
            default:
                return <BreakManagement key={`default-${refreshKey}`} onDataChange={fetchSummaryCounts} />;
        }
    };

    const tabs = [
        {
            id: 'breaks',
            step: '1. Katman',
            name: 'Mola Tanımları',
            subtitle: 'Ücretli/ücretsiz mola ve turnike kesinti kuralları',
            icon: Clock,
            count: counts.breaks,
            color: 'amber'
        },
        {
            id: 'shifts',
            step: '2. Katman',
            name: 'Vardiya Tanımları',
            subtitle: 'Sabit, pencereli, günlük ve haftalık esnek mesai',
            icon: Briefcase,
            count: counts.shifts,
            color: 'indigo'
        },
        {
            id: 'groups',
            step: '3. Katman',
            name: 'Vardiya Grupları',
            subtitle: 'Alternatif vardiya havuzları ve otomatik eşleşme',
            icon: Layers,
            count: counts.groups,
            color: 'purple'
        },
        {
            id: 'periods',
            step: '4. Katman',
            name: 'Çalışma Periyotları',
            subtitle: '7 günlük haftalık takvim ve dinamik hafta tatili',
            icon: CalendarDays,
            count: counts.periods,
            color: 'emerald'
        },
    ];

    return (
        <div className="w-full max-w-7xl mx-auto space-y-6 pb-8">
            {/* Üst Kurumsal Hero Banner */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 text-white p-6 shadow-xl border border-indigo-500/20">
                <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-purple-500/10 blur-2xl pointer-events-none" />
                <div className="absolute right-32 -bottom-16 w-56 h-56 rounded-full bg-blue-500/10 blur-2xl pointer-events-none" />

                <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="space-y-2 max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-200 text-xs font-semibold tracking-wide uppercase">
                            <Activity size={14} className="text-indigo-300" />
                            Puantaj Hesaplama Motoru Kural Merkezi
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                            Vardiya, Mola ve Çalışma Düzeni Mimarisi
                        </h1>
                        <p className="text-sm text-indigo-100/80 leading-relaxed">
                            Puantaj motoru <strong>4 katmanlı modüler hiyerarşi</strong> ile çalışır: tanımladığınız <strong>Molalar</strong> vardiyalara bağlanır, <strong>Vardiyalar</strong> vardiya gruplarında toplanır ve <strong>Çalışma Periyotları</strong> ile haftanın 7 gününe atanır. Tüm katmanları tek adımda kurmak için sağdaki sihirbazı kullanabilirsiniz.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0">
                        <button
                            onClick={() => setIsWizardOpen(true)}
                            className="px-6 py-3.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white rounded-xl font-bold flex items-center justify-center shadow-lg shadow-purple-500/30 transition-all hover:scale-[1.02] active:scale-95 border border-white/20"
                        >
                            <Wand2 size={20} className="mr-2.5" />
                            <span>Akıllı Mesai Sihirbazı</span>
                            <Sparkles size={16} className="ml-2 text-yellow-200" />
                        </button>
                    </div>
                </div>
            </div>

            {/* 4 Katmanlı İnteraktif Mimari Sekme Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                {tabs.map((tab, idx) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <div key={tab.id} className="relative flex items-stretch">
                            <button
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full text-left rounded-xl p-4 transition-all border flex flex-col justify-between ${
                                    isActive
                                        ? 'bg-white dark:bg-gray-800 border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-500/20 shadow-md'
                                        : 'bg-white/80 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700/80 hover:border-indigo-300 dark:hover:border-gray-600 hover:bg-white dark:hover:bg-gray-800'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-2.5">
                                    <div className="flex items-center gap-2.5">
                                        <div className={`p-2.5 rounded-lg ${
                                            isActive
                                                ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                        }`}>
                                            <Icon size={20} />
                                        </div>
                                        <div>
                                            <span className={`text-[11px] font-bold uppercase tracking-wider block ${
                                                isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'
                                            }`}>
                                                {tab.step}
                                            </span>
                                            <h3 className={`text-sm font-bold leading-snug ${
                                                isActive ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'
                                            }`}>
                                                {tab.name}
                                            </h3>
                                        </div>
                                    </div>
                                    <span className={`px-2.5 py-0.5 text-xs font-extrabold rounded-full ${
                                        isActive
                                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300'
                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                    }`}>
                                        {tab.count}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {tab.subtitle}
                                </p>
                            </button>
                            {idx < tabs.length - 1 && (
                                <div className="hidden lg:flex items-center justify-center -right-3 top-1/2 -translate-y-1/2 absolute z-10 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-400 pointer-events-none">
                                    <ChevronRight size={13} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Aktif Sekme İçeriği */}
            <div className="transition-all">
                {renderActiveTabContent()}
            </div>

            {/* Merkezi Mesai Sihirbazı Modalı */}
            <ShiftWizardModal
                isOpen={isWizardOpen}
                onClose={() => setIsWizardOpen(false)}
                onSave={() => {
                    setRefreshKey(prev => prev + 1);
                    setIsWizardOpen(false);
                }}
            />
        </div>
    );
}
